// Libs
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

// Components
import AttendeeCountModal from '../../../components/Kits/Modal/AttendeeCountModal/AttendeeCountModal';
import OrderConfirmation from '../../../components/OrderHistory/Modals/OrderConfirmation/OrderConfirmation';

// Actions
import { AddSharedKitToCart } from '../../../actions/sharedKits';
import { OpenModal, CloseModal } from '../../../actions/modal';

// Hooks
import useUI from '../../../hooks/useUI';

// Selectors
import { productsObjSelector } from '../../../store/selectors';

// Utils
import { getNotOrderableKitProducts } from '../utils/sharedKits';

// Constants
import {
  PRODUCT_OUT_OF_STACK,
  PRODUCTS_OUT_OF_STACK,
} from '../../../constants/common';
import {
  SKIPPED_PRODUCT_HEADER,
  SKIPPED_PRODUCTS_HEADER,
  skippedProductsMessage,
  orderKitWithoutProductsMessage,
} from '../../../constants/sharedKits';

// Constants
import {
  FORM_MODAL_HEIGHT_DESKTOP,
  FORM_MODAL_HEIGHT_MOBILE,
  FORM_MODAL_WIDTH_DESKTOP,
  FORM_MODAL_WIDTH_MOBILE,
  FORM_MODAL_WIDTH_TABLET,
} from '../../../constants/modals';

const useSharedKitOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isTabletMode, isMobileMode } = useUI();
  const productsObj = useSelector((state) => productsObjSelector(state));

  const navigateToCart = useCallback(
    () => navigate('/cart', { state: { navigateFrom: pathname } }),
    [navigate, pathname],
  );

  const toProducts = useCallback(
    (productIds) =>
      productIds.map(
        (productId) => productsObj?.[productId] || { product_id: productId },
      ),
    [productsObj],
  );

  const showSkippedProducts = useCallback(
    (productIds, acknowledgedProductIds = []) => {
      const unacknowledgedIds = productIds.filter(
        (productId) => !acknowledgedProductIds.includes(productId),
      );

      if (!unacknowledgedIds.length) {
        navigateToCart();
        return;
      }

      const skippedProducts = toProducts(unacknowledgedIds);
      const goToCart = () => {
        dispatch(CloseModal());
        navigateToCart();
      };

      dispatch(
        OpenModal({
          Component: OrderConfirmation,
          props: {
            unavailableProducts: skippedProducts,
            header:
              skippedProducts.length === 1
                ? SKIPPED_PRODUCT_HEADER
                : SKIPPED_PRODUCTS_HEADER,
            description: skippedProductsMessage(skippedProducts.length),
            cancelAction: goToCart,
            confirmAction: goToCart,
          },
          styles: {
            width: 'min(90vw, 651px)',
          },
        }),
      );
    },
    [dispatch, toProducts, navigateToCart],
  );

  const addToCart = useCallback(
    (kitId, attendeeCount, acknowledgedProductIds = []) => {
      dispatch(
        AddSharedKitToCart({
          kitId,
          attendeeCount,
          pathname,
          onUnavailableProducts: (productIds) =>
            showSkippedProducts(productIds, acknowledgedProductIds),
          navigateToCart,
        }),
      );
    },
    [dispatch, pathname, showSkippedProducts, navigateToCart],
  );

  const askAttendeeCount = useCallback(
    (kitId, acknowledgedProductIds = []) => {
      let width;
      if (isMobileMode) {
        width = FORM_MODAL_WIDTH_MOBILE;
      } else if (isTabletMode) {
        width = FORM_MODAL_WIDTH_TABLET;
      } else {
        width = FORM_MODAL_WIDTH_DESKTOP;
      }

      dispatch(
        OpenModal({
          Component: AttendeeCountModal,
          props: {
            onClickSave: (attendeeCount) =>
              addToCart(kitId, attendeeCount, acknowledgedProductIds),
            onClickCancel: () => dispatch(CloseModal()),
            paddingContent: {
              xl: '40px 40px 30px',
              lg: '40px 40px 30px',
              md: '24px 16px 24px',
            },
          },
          styles: {
            width,
            height: isMobileMode
              ? FORM_MODAL_HEIGHT_MOBILE
              : FORM_MODAL_HEIGHT_DESKTOP,
          },
        }),
      );
    },
    [dispatch, addToCart, isMobileMode, isTabletMode],
  );

  const submitKit = useCallback(
    (kit, acknowledgedProductIds = []) => {
      if (kit?.attendee_count_required) {
        askAttendeeCount(kit.id, acknowledgedProductIds);
        return;
      }
      addToCart(kit.id, undefined, acknowledgedProductIds);
    },
    [askAttendeeCount, addToCart],
  );

  const confirmNotOrderableProducts = useCallback(
    (kit, notOrderableProducts) => {
      const notOrderableIds = notOrderableProducts.map(
        ({ product }) => product,
      );
      const products = toProducts(notOrderableIds);

      dispatch(
        OpenModal({
          Component: OrderConfirmation,
          props: {
            unavailableProducts: products,
            header:
              products.length === 1
                ? PRODUCT_OUT_OF_STACK
                : PRODUCTS_OUT_OF_STACK,
            description: orderKitWithoutProductsMessage(products.length),
            cancelAction: () => dispatch(CloseModal()),
            confirmAction: () => {
              dispatch(CloseModal());
              submitKit(kit, notOrderableIds);
            },
          },
          styles: {
            width: 'min(90vw, 651px)',
          },
        }),
      );
    },
    [dispatch, toProducts, submitKit],
  );

  const onOrderClick = useCallback(
    (kit) => {
      const notOrderableProducts = getNotOrderableKitProducts(kit);

      if (notOrderableProducts.length) {
        confirmNotOrderableProducts(kit, notOrderableProducts);
        return;
      }

      submitKit(kit);
    },
    [confirmNotOrderableProducts, submitKit],
  );

  return { onOrderClick };
};

export default useSharedKitOrder;
